import React from "react";
import styled from "styled-components";
import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { RuleTableEditContext } from "../../../../rule-engine-core/Rule/Table/Rule/RuleTableEditContext";
import { RuleTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";

import { EditButton } from "../../../DesignUI/EditButton";

import { ActionWrapper, EditColumnAction, EditColumnWrapper } from "../../CommonStyles";
import { Error } from "../../CommonUI/Error";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { DeleteGrayIcon } from "../../../../CommonUI/Icon/DeleteGrayIcon";
import { RuleTableColumn } from "../../../../rule-engine-core/Rule/Table/Rule/Structure/RuleTableRuleColumn";
import { ColumnVariablePicker } from "../../CommonUI/ColumnVariablePicker";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";
import { UIStyleConstants } from "../../../../rule-engine-core/Common/UIStyleConstants";

interface RuleTableHeaderColumnProps {
    rule: RuleTableColumn;
    ruleDefinition: RuleTableConfiguration;
    editContext: RuleTableEditContext;
}

export const RuleTableHeaderColumn = (props: RuleTableHeaderColumnProps) => {
    const { rule, editContext, ruleDefinition } = props;

    const dataDefinition = ruleDefinition.getInputDefinition();

    const testContext = React.useContext(TestContext);

    const onSelVariable = () => {
        editContext.selectedColumnHeader = rule;
    };

    const onRemoveColumn = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        editContext.removeRuleColumn(rule);
    };

    return (
        <RuleTableHeaderDiv>
            <ObserveState
                listenTo={rule}
                properties={["path"]}
                control={() => {
                    let issues = ruleDefinition.getRuleColumnValidationIssues(rule);

                    return (
                        <RuleTableHeaderVariable
                            notSet={!rule.path}
                            onClick={onSelVariable}
                        >
                            {rule.name ?? rule.path ?? "Variable"}
                            {!testContext && (
                                <>
                                    <ActionWrapper>
                                        <EditButton
                                            onClick={onSelVariable}
                                            className="edit-button"
                                            dataTestId={`btn-open-select-variable`}
                                        />
                                    </ActionWrapper>
                                    <EditColumnWrapper className="edit-wrapper">
                                        {ruleDefinition.canChangeInputs && ruleDefinition.ruleColumns.length > 1 && (
                                            <EditColumnAction onClick={onRemoveColumn}>
                                                <DeleteGrayIcon />
                                            </EditColumnAction>
                                        )}
                                    </EditColumnWrapper>
                                    <ColumnVariablePicker
                                        rule={rule}
                                        editContext={editContext}
                                        allowNew={ruleDefinition.canChangeInputs}
                                        dataDefinition={dataDefinition}
                                        onSet={(rule, path) => {
                                            ruleDefinition.setRuleColumn(rule, path);
                                            StateManager.propertyChanged(rule, "path");
                                            StateManager.changed(ruleDefinition);
                                        }}
                                    />
                                </>
                            )}

                            <Error issues={issues} />
                        </RuleTableHeaderVariable>
                    );
                }}
            />
        </RuleTableHeaderDiv>
    );
};

const RuleTableHeaderDiv = styled.div`
    display: flex;
    flex-direction: row;
`;

const RuleTableHeaderVariable = styled.div<{ notSet: boolean }>`
    position: relative;

    min-width: 120px;
    min-height: 44px;

    width: 100%;
    height: 100%;

    background: ${UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${UIStyleConstants.BLUE_COLOR};
    box-sizing: border-box;
    border-radius: 2px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    padding: 14px 16px 13px;

    color: ${(props) => (props.notSet ? "" + UIStyleConstants.GRAY_LIGHTER_COLOR : UIStyleConstants.GRAY_DARK_COLOR)};

    cursor: pointer;

    .edit-button,
    .edit-wrapper {
        opacity: 0;

        transition: opacity 0.2s ease-in;
    }

    &:hover {
        .edit-button,
        .edit-wrapper {
            opacity: 1;
        }
    }
`;
